import type { ApiConnectionPlatform } from '../types/apiConnections.js';
import type {
  AccountConnectionHealth,
  PlatformHealth,
} from '../types/connectionHealth.js';

// Pure merge logic for Connection Health — folds observed fetch outcomes (from
// the jobs' `meta.failedAccounts` + the successfully-returned rows) into the
// durable per-account health block. No I/O, no Date (the observation time is
// injected). Shared by the capture service + tests.
//
// WRITE POLICY (minimises writes — the whole block is a "who needs reconnect"
// worklist, not a per-account per-poll ledger):
//   - A FAILING observation always updates that platform (status/error/time +
//     an incremented consecutive-failure count) → the doc is written.
//   - A HEALTHY observation only writes when it CLEARS an existing failing
//     platform (a recovery). A healthy observation on a platform that isn't
//     currently failing is a no-op — healthy-forever accounts never get a doc.
// So docs exist only for accounts that have failed at least once, and healthy
// observations churn no writes except on the transition back to healthy.

// How many DISTINCT failing runs an account+platform must accumulate before it
// surfaces as "needs reconnect" (the chip + the roster failing list). A dead
// token fails EVERY run and trips this fast; a transient blip fails once then
// recovers, so it never reaches the threshold. Sub-threshold failures are still
// recorded (failureCount ticks up) but not shown. Guard #2 of the
// false-positive fix (2026-07-15) — see classifyFetchError for guard #1.
export const CONNECTION_HEALTH_SURFACE_THRESHOLD = 2;

// ---------------------------------------------------------------------------
// Guard #1 — classify a failed fetch's error. A Zernio BACKEND wobble (a
// MongoDB pool reset, a Tinybird/vCPU limit, a 429/5xx) reports the WHOLE
// roster in meta.failedAccounts, but those accounts' tokens are fine. Only
// genuine AUTH/token failures should be recorded as failing; transient/infra
// failures are ignored entirely (not recorded, don't clear healthy).
//
// Order matters: check TRANSIENT first, because Zernio often wraps a backend
// error inside a generic "Failed to fetch posts from platform: <mongo…>"
// message — the transient marker inside must win over the generic wrapper.
// ---------------------------------------------------------------------------

export type FetchErrorClass = 'auth' | 'transient' | 'unknown';

// Zernio-side / network / rate-limit / 5xx signatures — NOT the account's auth.
const TRANSIENT_PATTERNS = [
  // MongoDB pool reset (the incident that motivated this fix)
  'mongodb',
  'connection pool',
  '27017',
  '<monitor>',
  'was cleared',
  // network
  'econnreset',
  'econnrefused',
  'enotfound',
  'etimedout',
  'timeout',
  'timed out',
  'socket hang up',
  'network error',
  // Raw-fetch (AbortSignal.timeout) + undici error shapes (2026-07-21, the scoped
  // comment poll). An aborted/timed-out request is NEVER an auth failure, so these
  // must read transient — otherwise a slow account that hits the 50s abort would be
  // recorded + surfaced as "connection failing" (the exact false positive).
  'aborterror',
  'timeouterror',
  'the operation was aborted',
  'aborted',
  'fetch failed', // undici's generic network wrapper
  // rate limit
  'rate limit',
  'ratelimit',
  'ratelimiterror',
  'too many requests',
  // 5xx / backend
  'internal_error',
  'internal server error',
  'service unavailable',
  'temporarily unavailable',
  'bad gateway',
  'gateway timeout',
  'tinybird',
  'vcpu',
];

// HTTP status codes that are always transient (rate limit / server / timeout).
const TRANSIENT_CODES = new Set(['408', '429', '500', '502', '503', '504']);

// Genuine auth/token failure signatures.
const AUTH_PATTERNS = [
  'invalid oauth',
  'access token',
  'error validating access token',
  'token expired',
  'expired token',
  'token has expired',
  'session has expired',
  'session expired',
  'oauthexception',
  'not authorized',
  'unauthorized',
  'permission',
  'revoked',
  // Ambiguous: a real dead token reports this persistently, so record it — the
  // persistence threshold (guard #2) prevents a one-off from crying wolf.
  'failed to fetch posts from platform',
];

// Meta auth codes (190 = invalid/expired token + its subcodes; 401 = HTTP
// unauthorized).
const AUTH_CODES = new Set(['190', '401']);

export function classifyFetchError(
  error?: string | null,
  code?: string | null,
): FetchErrorClass {
  const text = `${error ?? ''} ${code ?? ''}`.toLowerCase();
  const codeStr = code == null ? '' : String(code).trim();

  // Transient FIRST (a wrapped backend error must not read as auth).
  if (TRANSIENT_CODES.has(codeStr)) return 'transient';
  if (/^5\d\d$/.test(codeStr)) return 'transient';
  if (TRANSIENT_PATTERNS.some((p) => text.includes(p))) return 'transient';

  // Auth.
  if (AUTH_CODES.has(codeStr)) return 'auth';
  if (AUTH_PATTERNS.some((p) => text.includes(p))) return 'auth';

  return 'unknown';
}

// ---------------------------------------------------------------------------
// Raw-fetch scoped-list error classifier (2026-07-21). The scoped comment poll
// bypasses the SDK for a real 50s abort (the SDK can't — see
// _architect/findings/2026-07-21-zernio-no-request-timeout.md), so its errors are
// raw fetch throws + bare HTTP status codes, NOT the SDK's typed error classes
// that classifyFetchError's patterns were tuned to.
//
// THE RULE (Scott): fail toward TRANSIENT for anything unrecognised on this path.
// An unmapped raw error defaulting to 'unknown' would get RECORDED + surfaced — the
// false positive we're eliminating. So only a POSITIVELY-identified auth failure
// (401/403 or an auth message) records; timeouts are their own class (excluded
// entirely, never even fed to health); everything else → transient (dropped).
// ---------------------------------------------------------------------------

export type ScopedListErrorClass = 'auth' | 'transient' | 'timeout';

export function classifyScopedListError(input: {
  /** true when the 50s AbortSignal.timeout fired (DOMException name TimeoutError/AbortError). */
  isTimeout: boolean;
  /** the raw error message, if any. */
  message?: string | null;
  /** the bare HTTP status code, if the throw carried one. */
  status?: number | null;
}): ScopedListErrorClass {
  if (input.isTimeout) return 'timeout';
  const code = input.status != null ? String(input.status) : null;
  // Reuse the tuned auth/transient patterns, but INVERT the default: unknown →
  // transient. Only a positively-classified auth failure records.
  return classifyFetchError(input.message ?? null, code) === 'auth' ? 'auth' : 'transient';
}

export type HealthObservationKind = 'failing' | 'healthy';

// ---------------------------------------------------------------------------
// Community-scope gate — the "failing connections" signal is fed ENTIRELY by the
// community poll's roster-wide listInboxComments failedAccounts, i.e. it is a
// COMMUNITY-INBOX READ failure, not a dead connection (proven live 2026-07-17:
// Snap Fitness Carina flagged "Failed to fetch posts ×10" while posting to its
// page SUCCEEDED). So a failing observation is only meaningful for accounts
// actually running the inbox — drop failing observations for non-communityEnabled
// accounts before they're recorded. A HEALTHY observation always passes (it only
// ever CLEARS an existing failing row — a no-op otherwise — so it can recover a
// pre-fix non-community row). The ad-account DRIFT signal is unaffected (it's a
// config comparison across all live Meta accounts, not this read).
// ---------------------------------------------------------------------------

export function shouldRecordObservation(
  kind: HealthObservationKind,
  communityEnabled: boolean,
): boolean {
  return kind === 'failing' ? communityEnabled : true;
}

export type HealthObservation = {
  platform: ApiConnectionPlatform;
  kind: HealthObservationKind;
  // Failure detail (ignored for healthy observations).
  error?: string | null;
  code?: string | null;
};

// Collapse multiple observations for the same platform into one — a FAILING
// observation always wins over a healthy one (a platform that both returned a
// row AND appeared in failedAccounts is treated as failing; the fetch demonstrably
// broke). Keeps the first failing observation's error/code.
export function reduceObservationsByPlatform(
  observations: HealthObservation[],
): Map<ApiConnectionPlatform, HealthObservation> {
  const byPlatform = new Map<ApiConnectionPlatform, HealthObservation>();
  for (const obs of observations) {
    const existing = byPlatform.get(obs.platform);
    if (!existing) {
      byPlatform.set(obs.platform, obs);
      continue;
    }
    // failing beats healthy; two failings → keep the first (stable).
    if (existing.kind === 'failing') continue;
    if (obs.kind === 'failing') byPlatform.set(obs.platform, obs);
  }
  return byPlatform;
}

function nextFailing(
  prev: PlatformHealth | undefined,
  at: string,
  error: string | null,
  code: string | null,
): PlatformHealth {
  const consecutive = prev?.status === 'failing' ? prev.failureCount : 0;
  return {
    status: 'failing',
    lastError: error ?? prev?.lastError ?? null,
    lastErrorCode: code ?? prev?.lastErrorCode ?? null,
    lastFailureAt: at,
    lastHealthyAt: prev?.lastHealthyAt ?? null,
    failureCount: consecutive + 1,
    lastCheckedAt: at,
  };
}

function nextHealthy(prev: PlatformHealth, at: string): PlatformHealth {
  return {
    status: 'healthy',
    // Retain the last failure detail as history (so the view can show "was
    // failing, recovered").
    lastError: prev.lastError,
    lastErrorCode: prev.lastErrorCode,
    lastFailureAt: prev.lastFailureAt,
    lastHealthyAt: at,
    failureCount: 0,
    lastCheckedAt: at,
  };
}

/**
 * Merge a batch of observations for ONE account into its health doc. Returns the
 * new doc when something changed, or null when nothing changed (skip the write).
 * `prev` is the currently-stored doc (or null when the account has no doc yet).
 */
export function mergeObservationsIntoHealth(
  prev: AccountConnectionHealth | null,
  accountId: string,
  observations: HealthObservation[],
  at: string,
): AccountConnectionHealth | null {
  const collapsed = reduceObservationsByPlatform(observations);
  if (collapsed.size === 0) return null;

  const platforms: AccountConnectionHealth['platforms'] = { ...(prev?.platforms ?? {}) };
  let changed = false;

  for (const [platform, obs] of collapsed) {
    const current = platforms[platform];
    if (obs.kind === 'failing') {
      platforms[platform] = nextFailing(current, at, obs.error ?? null, obs.code ?? null);
      changed = true;
    } else if (current && current.status === 'failing') {
      // Recovery — only meaningful when the platform was failing.
      platforms[platform] = nextHealthy(current, at);
      changed = true;
    }
    // healthy observation on a non-failing / absent platform → no-op.
  }

  if (!changed) return null;
  return { accountId, platforms, updatedAt: at };
}

// True when a platform's failing state has PERSISTED enough runs to surface
// (guard #2). status must be failing AND failureCount must have reached the
// threshold. Pure predicate — unit-tested.
export function platformIsSurfaced(
  state: Pick<PlatformHealth, 'status' | 'failureCount'> | undefined,
  threshold: number = CONNECTION_HEALTH_SURFACE_THRESHOLD,
): boolean {
  return state?.status === 'failing' && state.failureCount >= threshold;
}

/**
 * The platforms that should SURFACE as failing (the chip + roster view) — in a
 * failing state AND past the persistence threshold. A single sub-threshold
 * failure is recorded but NOT surfaced (pass threshold 1 for the raw set).
 */
export function failingPlatforms(
  health: Pick<AccountConnectionHealth, 'platforms'> | null | undefined,
  threshold: number = CONNECTION_HEALTH_SURFACE_THRESHOLD,
): ApiConnectionPlatform[] {
  if (!health) return [];
  return (Object.keys(health.platforms) as ApiConnectionPlatform[]).filter((p) =>
    platformIsSurfaced(health.platforms[p], threshold),
  );
}

/** True when any platform surfaces as failing (past the persistence threshold). */
export function isAccountConnectionFailing(
  health: Pick<AccountConnectionHealth, 'platforms'> | null | undefined,
  threshold: number = CONNECTION_HEALTH_SURFACE_THRESHOLD,
): boolean {
  return failingPlatforms(health, threshold).length > 0;
}

// ---------------------------------------------------------------------------
// Platform scoping for thread-level surfaces (Part E, 2026-07-19).
//
// The health doc is ACCOUNT-level (a bag of per-platform states), so rendering
// it unscoped on a thread showed a Google Business failure on an Instagram
// thread. A thread only cares about the platform(s) IT is delivered through.
// ---------------------------------------------------------------------------

/**
 * The health platform(s) a thread's channel actually depends on. Mostly 1:1;
 * the exception is `metaads` (ad comments), which is moderated through the
 * underlying FB Page / IG connection, so a Page failure is genuinely relevant
 * to an ad-comment thread.
 */
export function healthPlatformsForChannel(
  channel: 'facebook' | 'instagram' | 'googlebusiness' | 'metaads',
): ApiConnectionPlatform[] {
  switch (channel) {
    case 'facebook':
      return ['facebook'];
    case 'instagram':
      return ['instagram'];
    case 'googlebusiness':
      return ['googlebusiness'];
    case 'metaads':
      // Ad comments act through the connected Page / IG identity.
      return ['metaads', 'facebook', 'instagram'];
  }
}

/**
 * Failing platforms RELEVANT to one channel — the thread-scoped chip input.
 * A GBP failure therefore never surfaces on an IG/FB thread (and vice versa).
 */
export function failingPlatformsForChannel(
  health: Pick<AccountConnectionHealth, 'platforms'> | null | undefined,
  channel: 'facebook' | 'instagram' | 'googlebusiness' | 'metaads',
  threshold: number = CONNECTION_HEALTH_SURFACE_THRESHOLD,
): ApiConnectionPlatform[] {
  const relevant = new Set<ApiConnectionPlatform>(healthPlatformsForChannel(channel));
  return failingPlatforms(health, threshold).filter((p) => relevant.has(p));
}
